// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for editing an existing pipeline's name and description.
 */
import { useState, useEffect } from 'react'
import { Input, Textarea } from '@/components/ui'
import { Field, FormDialog } from '@/components/forms'
import type { Pipeline } from '../types'

export interface EditPipelineData {
  name: string
  description?: string
}

interface EditPipelineDialogProps {
  pipeline: Pipeline
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (data: EditPipelineData) => void
  isPending: boolean
}

export function EditPipelineDialog({
  pipeline,
  open,
  onOpenChange,
  onSave,
  isPending,
}: EditPipelineDialogProps) {
  const [name, setName] = useState(pipeline.name)
  const [description, setDescription] = useState(pipeline.description ?? '')

  useEffect(() => {
    setName(pipeline.name)
    setDescription(pipeline.description ?? '')
  }, [pipeline])

  const handleSubmit = () => {
    onSave({
      name,
      description: description || undefined,
    })
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Pipeline"
      description="Update pipeline details."
      onSubmit={handleSubmit}
      submitting={isPending}
      canSubmit={name.trim().length > 0}
      submitTestId="builder-pipeline-edit-submit"
    >
      <Field label="Pipeline ID" htmlFor="edit-id">
        <Input id="edit-id" value={pipeline.id} disabled readOnly />
      </Field>
      <Field label="Name" htmlFor="edit-name">
        <Input
          id="edit-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isPending}
          required
          autoFocus
          data-testid="builder-pipeline-edit-name"
        />
      </Field>
      <Field label="Description" htmlFor="edit-description">
        <Textarea
          id="edit-description"
          placeholder="Optional description..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isPending}
          rows={3}
        />
      </Field>
    </FormDialog>
  )
}
